import * as React from 'react';
import { useState, useEffect } from 'react';
import cx from 'classnames';
import Text from 'components/Text';
import Textarea from 'components/Textarea';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import Icon from 'components/Icon';
import LoadingIndicator from 'components/LoadingIndicator';
import { Intent, TranslationType } from 'translations/types';
import { TranslationPreviewHeader } from 'translations/components/TranslationPreviewHeader';
import TranslationPreviewOriginalText from 'translations/components/TranslationPreviewOriginalText';
import { TranslationPreviewFiles } from 'translations/components/TranslationPreviewFiles';
import { getLanguagePreviewTitle } from 'translations/utils';
import 'translations/components/styles/TranslationPreview.scss';

type Props = {
  canEdit: boolean;
  languageCode: string;
  filePreview?: React.ReactNode;
  textBeforeTranslation: string;
  currentTranslation: TranslationType;
  translationState: TranslationState;
  handleTranslationChange: (languageCode: string, newTranslation: TranslationType) => void;
  getUpdatedTranslation: (languageCode: string) => void;
  onClose: () => void;
};

export type State = {
  intentMenuIsOpen: boolean;
  fullOriginalTextDisplayed: boolean;
};

export enum TranslationState {
  None,
  Empty,
  Loading,
  LimitError,
  ServerError,
  Success,
}

function TranslationPreview({
  canEdit,
  languageCode,
  filePreview,
  currentTranslation,
  translationState,
  textBeforeTranslation,
  handleTranslationChange,
  getUpdatedTranslation,
  onClose,
}: Props) {
  const {
    intent: currentIntent,
    userEditedTranslation: currentUserEditedTranslation,
    autoTranslation: currentAutoTranslation,
    originalText,
  } = currentTranslation;

  const [intentMenuIsOpen, setIntentMenuIsOpen] = useState(false);
  const [fullOriginalTextDisplayed, setFullOriginalTextDisplayed] = useState(false);

  const noPreferenceSet = languageCode === 'none';
  const isEmptyTranslation = translationState === TranslationState.Empty;
  const displayEditingOptions = canEdit && !noPreferenceSet;

  useEffect(() => {
    if (
      currentIntent === 'Autotranslated' &&
      originalText.trim() !== textBeforeTranslation.trim() &&
      translationState === TranslationState.Success
    ) {
      getUpdatedTranslation(languageCode);
    }
  });

  // close if click outside the box unless the click is on the translate buttons, which has its own behavior

  const toggleIntentMenuIsOpen = () => {
    setIntentMenuIsOpen(!intentMenuIsOpen);
  };

  const toggleDisplayFullOriginalText = (expandText: boolean) => {
    setFullOriginalTextDisplayed(expandText);
  };

  const getTextToShow = () => {
    switch (currentIntent) {
      case 'Autotranslated':
        return currentAutoTranslation;
      case 'Edited':
        return currentUserEditedTranslation ? currentUserEditedTranslation : currentAutoTranslation;
      case 'Off':
      default:
        return '';
    }
  };

  const handleIntentChange = (newIntent?: Intent) => {
    if (newIntent) {
      handleTranslationChange(languageCode, { ...currentTranslation, intent: newIntent });
      setIntentMenuIsOpen(false);
    }
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text: string = event.target.value;
    handleTranslationChange(languageCode, {
      ...currentTranslation,
      userEditedTranslation: text,
      intent: 'Edited',
    });
  };

  const renderTranslationEmptyView = () => {
    const infoText = displayEditingOptions ? 'review and edits' : 'review';
    return (
      <div className="TranslationPreview-translation-empty">
        <Text
          data-test-selector="preview-translate-empty-editor"
          align="center"
          size="xsmall"
          className="TranslationPreview-translation-info-text">
          After you compose a message, the translation will appear here for your {infoText}.
        </Text>
      </div>
    );
  };

  const renderTranslationLimitErrorView = () => {
    return (
      <div className="TranslationPreview-translation-empty">
        <Text
          data-test-selector="preview-translate-limit-error-editor"
          align="center"
          size="xsmall"
          className="TranslationPreview-translation-info-text">
          To see the translation, reduce your message to below the character limit.
        </Text>
      </div>
    );
  };

  const renderTranslationServerErrorView = () => {
    return (
      <div className="TranslationPreview-translation-server-error">
        <Icon name="explanation-circle" size="medium" />
        <Text
          data-test-selector="preview-translate-server-error-editor"
          align="center"
          size="xsmall"
          className="TranslationPreview-translation-info-text">
          Error loading translation &mdash;{' '}
          <Link onClick={() => getUpdatedTranslation(languageCode)}>Retry</Link>
        </Text>
      </div>
    );
  };

  const renderTranslationLoadingView = () => {
    return (
      <div className="TranslationPreview-translation-empty">
        <LoadingIndicator
          data-test-selector="preview-translate-loading-editor"
          size="medium"
          color="percussion"
        />
      </div>
    );
  };

  const renderTranslationView = () => {
    return (
      <div data-test-selector="preview-translate-editor">
        <TranslationPreviewOriginalText
          messageText={textBeforeTranslation}
          activeIntent={currentIntent}
          forceFullTextDisplay={fullOriginalTextDisplayed}
          toggleTextExpanded={toggleDisplayFullOriginalText}
        />
        <Textarea
          data-test-selector={'preview-translate-editor-translated-text'}
          className="TranslationPreview-translation-text"
          value={getTextToShow() || ''}
          autogrow
          onChange={handleTextChange}
        />
      </div>
    );
  };

  const renderTranslationEditor = () => {
    if (noPreferenceSet) {
      return renderStatic();
    }

    switch (translationState) {
      case TranslationState.Empty:
        return renderTranslationEmptyView();
      case TranslationState.Loading:
        return renderTranslationLoadingView();
      case TranslationState.LimitError:
        return renderTranslationLimitErrorView();
      case TranslationState.ServerError:
        return renderTranslationServerErrorView();
      case TranslationState.Success:
      default:
        return displayEditingOptions ? renderTranslationView() : renderStatic();
    }
  };

  const renderStatic = () => {
    return (
      <Text
        className={cx(
          'TranslationPreview-translation-text',
          noPreferenceSet && 'TranslationPreview-no-preference-text'
        )}
        data-test-selector={'static-preview-translate'}>
        {currentAutoTranslation}{' '}
        {noPreferenceSet && (
          <Link href={LinkConstants.TRANSLATIONS_TOOLTIP_NO_PREFERENCE_HELP} target="_blank">
            Learn more
          </Link>
        )}
      </Text>
    );
  };

  return (
    <div className="TranslationPreview-outer">
      <TranslationPreviewHeader
        languageTitle={getLanguagePreviewTitle(languageCode)}
        noTranslationExists={isEmptyTranslation || noPreferenceSet}
        displayEditingOptions={
          displayEditingOptions && translationState == TranslationState.Success
        }
        activeIntent={currentIntent}
        intentMenuOpen={intentMenuIsOpen}
        updateIntent={handleIntentChange}
        toggleIntentMenuOpen={toggleIntentMenuIsOpen}
        onClose={onClose}
      />
      {renderTranslationEditor()}
      {filePreview && <TranslationPreviewFiles filePreview={filePreview} />}
    </div>
  );
}

export { TranslationPreview, Props };
